/** Minimal DER walker for the ASN.1 tree pane.
 *
 * We keep our own walker (rather than asn1js) because the tree needs exact
 * absolute byte offsets to synchronize with the hex pane, and defensive
 * depth/size limits since .tsr bytes are hostile input.
 */

export interface DerNode {
  /** absolute offset of the tag byte */
  offset: number;
  /** total encoded length including header */
  length: number;
  headerLength: number;
  tagClass: "universal" | "application" | "context" | "private";
  tagNumber: number;
  tagName: string;
  constructed: boolean;
  children: DerNode[];
  /** short printable preview for primitive values */
  preview?: string;
}

const UNIVERSAL_NAMES: Record<number, string> = {
  1: "BOOLEAN",
  2: "INTEGER",
  3: "BIT STRING",
  4: "OCTET STRING",
  5: "NULL",
  6: "OBJECT IDENTIFIER",
  10: "ENUMERATED",
  12: "UTF8String",
  16: "SEQUENCE",
  17: "SET",
  19: "PrintableString",
  20: "TeletexString",
  22: "IA5String",
  23: "UTCTime",
  24: "GeneralizedTime",
  30: "BMPString",
};

const MAX_DEPTH = 32;
const MAX_NODES = 20_000;

class Walker {
  nodes = 0;
  constructor(private data: Uint8Array) {}

  parse(offset: number, end: number, depth: number): DerNode[] {
    const out: DerNode[] = [];
    let pos = offset;
    while (pos < end) {
      if (++this.nodes > MAX_NODES) throw new Error("ASN.1 structure too large");
      const node = this.parseOne(pos, end, depth);
      out.push(node);
      pos = node.offset + node.length;
    }
    return out;
  }

  parseOne(offset: number, end: number, depth: number): DerNode {
    if (depth > MAX_DEPTH) throw new Error("ASN.1 nesting too deep");
    const data = this.data;
    if (offset >= end) throw new Error("truncated DER");
    const tag = data[offset];
    const tagClass = (["universal", "application", "context", "private"] as const)[
      tag >> 6
    ];
    const constructed = (tag & 0x20) !== 0;
    let tagNumber = tag & 0x1f;
    let pos = offset + 1;
    if (tagNumber === 0x1f) {
      // high tag numbers — rare in PKI; walk the base-128 bytes
      tagNumber = 0;
      while (pos < end) {
        const b = data[pos++];
        tagNumber = (tagNumber << 7) | (b & 0x7f);
        if ((b & 0x80) === 0) break;
      }
    }
    if (pos >= end) throw new Error("truncated DER header");
    let lenByte = data[pos++];
    let contentLength: number;
    if ((lenByte & 0x80) === 0) {
      contentLength = lenByte;
    } else {
      const numBytes = lenByte & 0x7f;
      if (numBytes === 0 || numBytes > 4) throw new Error("unsupported DER length");
      contentLength = 0;
      for (let i = 0; i < numBytes; i++) {
        if (pos >= end) throw new Error("truncated DER length");
        contentLength = contentLength * 256 + data[pos++];
      }
    }
    const headerLength = pos - offset;
    const contentEnd = pos + contentLength;
    if (contentEnd > end) throw new Error("DER length exceeds data");

    const tagName =
      tagClass === "universal"
        ? (UNIVERSAL_NAMES[tagNumber] ?? `UNIVERSAL ${tagNumber}`)
        : tagClass === "context"
          ? `[${tagNumber}]`
          : `${tagClass.toUpperCase()} ${tagNumber}`;

    const node: DerNode = {
      offset,
      length: headerLength + contentLength,
      headerLength,
      tagClass,
      tagNumber,
      tagName,
      constructed,
      children: [],
    };

    if (constructed) {
      node.children = this.parse(pos, contentEnd, depth + 1);
    } else {
      node.preview = this.previewValue(tagNumber, tagClass, pos, contentLength);
    }
    return node;
  }

  previewValue(
    tagNumber: number,
    tagClass: string,
    start: number,
    length: number
  ): string {
    const bytes = this.data.subarray(start, start + Math.min(length, 64));
    if (tagClass !== "universal") return hexPreview(bytes, length);
    switch (tagNumber) {
      case 1:
        return bytes[0] === 0 ? "FALSE" : "TRUE";
      case 2: {
        if (length <= 6) {
          let v = 0n;
          for (const b of this.data.subarray(start, start + length)) {
            v = (v << 8n) | BigInt(b);
          }
          return v.toString();
        }
        return hexPreview(bytes, length);
      }
      case 5:
        return "";
      case 6:
        return decodeOid(this.data.subarray(start, start + length));
      case 12:
      case 19:
      case 22:
      case 23:
      case 24:
        return safeAscii(bytes, length);
      default:
        return hexPreview(bytes, length);
    }
  }
}

function hexPreview(bytes: Uint8Array, totalLength: number): string {
  let out = "";
  for (const b of bytes) out += b.toString(16).padStart(2, "0");
  return totalLength > bytes.length ? `${out}… (${totalLength} bytes)` : out;
}

function safeAscii(bytes: Uint8Array, totalLength: number): string {
  let out = "";
  for (const b of bytes) {
    out += b >= 0x20 && b < 0x7f ? String.fromCharCode(b) : ".";
  }
  return totalLength > bytes.length ? `${out}…` : out;
}

export function decodeOid(bytes: Uint8Array): string {
  // Decode base-128 groups; the first group encodes the first two arcs.
  const groups: bigint[] = [];
  let acc = 0n;
  for (const b of bytes) {
    acc = (acc << 7n) | BigInt(b & 0x7f);
    if ((b & 0x80) === 0) {
      groups.push(acc);
      acc = 0n;
    }
  }
  if (groups.length === 0) return "";
  const [head, ...rest] = groups;
  const parts: string[] =
    head < 40n
      ? ["0", head.toString()]
      : head < 80n
        ? ["1", (head - 40n).toString()]
        : ["2", (head - 80n).toString()];
  for (const g of rest) parts.push(g.toString());
  return parts.join(".");
}

export function walkDer(data: Uint8Array): DerNode {
  const walker = new Walker(data);
  const roots = walker.parse(0, data.length, 0);
  if (roots.length !== 1) {
    throw new Error(`expected one top-level DER value, found ${roots.length}`);
  }
  return roots[0];
}
